/// <amd-module name="SearchUI/Components/PathSelector/PathSelectorMenu" />

import * as React from "react";
import * as ReactDOM from "react-dom";
import { css } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IListItemDetails, List, ListItem, ListSelection } from "VSSUI/List";
import { IPathSelectorMenuProps } from "SearchUI/Components/PathSelector/PathSelector.Props";
import * as Resources from "SearchUI/Search.Resources.SearchUI";

import "VSS/LoaderPlugins/Css!SearchUI/Components/PathSelector/PathSelectorMenu";

export interface IPathSelectorMenuState {
    width: number;

    height: number;

    lastMouseX: number;

    lastMouseY: number;

    resizing: boolean;

    resizeHappened: boolean;
}

/**
 * This is set kept in mind the maximun height of the dropdown(=224px), and each element withing dropdown takes 32px,
 * So ideally list should render 224/32 = 7 items per page. Altering this value may modify the behavior when the next item is brought within the scroll view.
 */
const ITEMS_TO_RENDER_PER_LIST_PAGE = 7,
    PATH_ELEMENT_HEIGHT = 32,
    Path_DROPDOWN_DEFAULT_MAX_HEIGHT = 295;

export class PathSelectorMenu extends React.Component<IPathSelectorMenuProps, IPathSelectorMenuState> {
    private _itemList = React.createRef<List<any>>();
    private _selection = new ListSelection();

    private _viewState: IPathSelectorMenuState;

    private _pathSelectorMenu: HTMLElement;

    private _mounted: boolean;

    constructor(props: IPathSelectorMenuProps) {
        super(props);
        this.state = this._viewState = {
            resizing: false,
            resizeHappened: false
        } as IPathSelectorMenuState;

        this._mounted = false;
    }

    public render(): JSX.Element {
        const { items, id } = this.props,
            itemsCount = items.length;
        let minHeight: number,
            pathControlStyle = {
                width: this.state.width + "px",
                height: this.state.height + "px"
            },
            elementListStyle = {};

        // In case of only one element 32px is not enought to contain Element and the message
        // So adding +1 if elements are less than 7
        minHeight = itemsCount > ITEMS_TO_RENDER_PER_LIST_PAGE ? Path_DROPDOWN_DEFAULT_MAX_HEIGHT : (itemsCount + 1) * PATH_ELEMENT_HEIGHT;

        if (this.state.resizeHappened) {
            // Calculating height of the path control from the bottom of the window
            pathControlStyle["maxHeight"] = document.body.clientHeight - this._pathSelectorMenu.getBoundingClientRect().top + "px";
            pathControlStyle["maxWidth"] = document.body.clientWidth - this._pathSelectorMenu.getBoundingClientRect().left + "px";
            pathControlStyle["minHeight"] = minHeight + "px";
            elementListStyle["maxHeight"] = "none";
        }

        return (
            // Ease out the dropdown when the control is not mounted yet.
            // On subsequent render "easing-out" the dropdown gives a blurry effect
            // and is quited visible since the dropdown stays
            <div
                className={css("path-Menu--container", !this._mounted && "ease-out")}
                ref={element => {
                    this._pathSelectorMenu = element;
                }}
                style={pathControlStyle}
            >
                <div 
                    className={css("path-List")}
                    tabIndex={-1}
                    style={elementListStyle}
                    id={id}
                    role="listbox"
                    aria-label={Resources.PathMenuContainerAriaLabel}>
                    <List
                        itemProvider={new ArrayItemProvider(items)}
                        pageSize={ITEMS_TO_RENDER_PER_LIST_PAGE}
                        renderRow={this._onRenderRow}
                        ref={this._itemList}
                        selection={this._selection}
                        width="100%"
                        role="list"
                    />
                </div>
                <div className={css("message")} onMouseDown={this._onHelperMessageMouseDown} aria-live="assertive">
                    {this.props.footerMessageElement}
                </div>
                <div className={css("bowtie-icon", "resizer", "bowtie-corner-resize")} tabIndex={-1} onMouseDown={this._onResizerMouseDown} />
            </div>
        );
    }

    /**
     * On any update of the dropdown component, we need to re-render the list in order to update the highlighted element
     * Since the highlighted is done in a function callback and does not trigger re-render, we need to force update the list.
     * @param prevProps
     * @param prevState
     */
    public componentWillReceiveProps(nextProps: IPathSelectorMenuProps): void {
        if (nextProps.currentItemIndex !== this.props.currentItemIndex) {
            let scrollToIndex = nextProps.currentItemIndex >= 0 ? nextProps.currentItemIndex : 0;
            this._selection.select(scrollToIndex);
        }
    }

    public componentWillUnmount(): void {
        const { ownerDocument } = ReactDOM.findDOMNode(this);
        ownerDocument.removeEventListener("mousemove", this._onMouseMove);
        ownerDocument.removeEventListener("mouseup", this._onMouseUp);
    }

    public componentDidMount(): void {
        this._mounted = true;
    }

    /**
     * Function called when the mouse event happens on the helper message in the dropdown.
     * The mouse operation is made NoOp by supressing the event.
     * @param evt
     */
    private _onHelperMessageMouseDown = evt => {
        evt.stopPropagation();
        evt.preventDefault();
    };

    /**
     * Function called when a cell of the List is rendered
     * @param evt
     */
    private _onRenderRow = (index: number, item: any, details: IListItemDetails<any>, key?: string) => {
        const highlighted: boolean = this.props.currentItemIndex === index;
        return (
            <ListItem details={details} index={index} key={"list-item" + index}>
                <div
                    className={css("path-Element flex-row", highlighted && "highlighted")}
                    role="listitem"
                    onClick={() => this.props.onItemClick(item)}
                >
                    {this.props.onItemRender(item)}
                </div>
            </ListItem>
        );
    };

    /**
     * Binds Mouse move and Mouse up events to the resizer div and stores intial set of values required for resizing
     * @param evt
     */
    private _onResizerMouseDown = (evt: any) => {
        this._viewState = {
            width: this._pathSelectorMenu.getBoundingClientRect().width,
            height: this._pathSelectorMenu.getBoundingClientRect().height,
            lastMouseX: evt.clientX,
            lastMouseY: evt.clientY,
            resizing: true
        } as IPathSelectorMenuState;

        const { ownerDocument } = ReactDOM.findDOMNode(this);

        evt.preventDefault();
        evt.stopPropagation();

        ownerDocument.addEventListener("mousemove", this._onMouseMove);
        ownerDocument.addEventListener("mouseup", this._onMouseUp);

        this.setState(this._viewState);
    };

    /**
     * Updates Width and height of the container as the mouse moves, while resizing the container
     * @param evt
     */
    private _onMouseMove = (evt: any) => {
        if (this.state.resizing) {
            let width = this.state.width + evt.clientX - this.state.lastMouseX,
                height = this.state.height + evt.clientY - this.state.lastMouseY;

            this._viewState.width = width;
            this._viewState.height = height;
            this._viewState.lastMouseX = evt.clientX;
            this._viewState.lastMouseY = evt.clientY;

            if (!this.state.resizeHappened) {
                this._viewState.resizeHappened = true;
            }

            this.setState(this._viewState);
        }
    };

    /**
     * Remove resize mouse event listeners on mouse up
     * @param evt
     */
    private _onMouseUp = (evt: any) => {
        this._viewState.resizing = false;
        const { ownerDocument } = ReactDOM.findDOMNode(this);
        ownerDocument.removeEventListener("mousemove", this._onMouseMove);
        ownerDocument.removeEventListener("mouseup", this._onMouseUp);
        this.setState(this._viewState);
    };
}
